import * as Sentry from '@sentry/nextjs'
import { NextPageWithLayout } from 'types/NextPageWithLayout'
import { PageContext } from 'types/PageContext'
import {
  BrandProductSortOrder,
  BrandReviewsQuery,
  BrandReviewsQueryVariables,
  GetBrandBySlugQuery,
  GetBrandBySlugQueryVariables,
  GetBrandRedirectSlugQuery,
  GetBrandRedirectSlugQueryVariables,
  ReviewQuery,
  ReviewQueryVariables,
  ReviewableBrandProductsQuery,
  ReviewableBrandProductsQueryVariables
} from 'types/graphql'
import { assertExists } from 'utils/assert'
import { nextRedirect } from 'utils/nextRedirect'
import { ReviewFilterParams } from 'modules/brands/BrandPage'
import { reviewableBrandProducts } from 'modules/brands/queries/useBrandReviewableProducts'
import { brandReviewsQuery } from 'modules/reviews/list/queries/brandReviews.graphql'
import { reviewQuery } from 'modules/reviews/list/queries/review.graphql'
import { BrandReviewsUrlParam } from 'modules/reviews/utils/buildReviewFilterPath'
import { DEFAULT_SORT_OPTION, paramToSortOrder } from 'modules/reviews/utils/sortOrder'
import { getBrandQuery } from '../../queries/getBrand.graphql'
import { getBrandRedirectQuery } from '../../queries/getBrandRedirect.graphql'

interface Props {
  brand: GetBrandBySlugQuery['brand']
  params: ReviewFilterParams
}

export type ShowBrandPage = NextPageWithLayout<Props>

export const getShowBrandInitialProps =
  (redirectPath = '/reviews') =>
  async ({ asPath, query: { slug, reviewId }, res, apolloClient }: PageContext): Promise<Props> => {
    assertExists(slug)

    const { searchParams } = new URL(`https://thingtesting.com${asPath}`)
    const brandProductIds = searchParams.getAll(BrandReviewsUrlParam.BRAND_PRODUCT_IDS) || []
    const order = paramToSortOrder(searchParams.get(BrandReviewsUrlParam.ORDER), DEFAULT_SORT_OPTION.key)

    const { data } = await apolloClient.query<GetBrandBySlugQuery, GetBrandBySlugQueryVariables>({
      query: getBrandQuery,
      variables: {
        slug: slug.toString()
      }
    })

    const prefetches: Promise<any>[] = []

    // If there was no brand found, try to find a brand with this slug as an old slug and redirect to it.
    if (!data.brand) {
      try {
        const { data: redirectData } = await apolloClient.query<GetBrandRedirectSlugQuery, GetBrandRedirectSlugQueryVariables>({
          query: getBrandRedirectQuery,
          variables: {
            slug: slug.toString()
          }
        })

        if (redirectData?.findBrandRedirect?.slug) {
          return nextRedirect({ to: `/brands/${redirectData.findBrandRedirect.slug}${redirectPath}`, method: 'replace', res })
        }
      } catch (err) {
        Sentry.captureException(err)
        return { brand: null } as Props
      }
    }

    if (data.brand) {
      // Cache reviews
      prefetches.push(
        apolloClient.query<BrandReviewsQuery, BrandReviewsQueryVariables>({
          query: brandReviewsQuery,
          variables: {
            brandId: data.brand.id,
            take: 10,
            exclude: [reviewId as string].filter((i): i is string => Boolean(i))
          }
        })
      )

      prefetches.push(
        apolloClient.query<ReviewableBrandProductsQuery, ReviewableBrandProductsQueryVariables>({
          query: reviewableBrandProducts,
          variables: {
            brandId: data.brand.id,
            onlyWithReviews: true,
            order: BrandProductSortOrder.Popular
          }
        })
      )
    }

    if (reviewId) {
      prefetches.push(
        apolloClient.query<ReviewQuery, ReviewQueryVariables>({
          query: reviewQuery,
          variables: {
            id: reviewId.toString()
          }
        })
      )
    }

    await Promise.all(prefetches)

    return {
      brand: data.brand,
      params: {
        brandProductIds,
        reviewId: reviewId as string,
        order
      }
    }
  }
